// InternalTrackers.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    Grid,
    Modal,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TablePagination,
    Checkbox,
    Chip,
    TableContainer,
    IconButton,
    Menu,
    Tooltip
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Layout from '../Layout';
import { useAuth } from '../context/userContext';
import { canonicalRisk } from '../utils/trackerNormalization';


const CATEGORY_OPTIONS = [
    'Conditions',
    'Displays',
    'Filings',
    'Intimations & Notices',
    'Licenses & Registrations',
    'Meetings',
    'Others',
    'Payments',
    'Prohibitions & Restrictions',
    'Registers',
    'Internal',
    'Legal Update'
];
const RISK_OPTIONS = ['Low', 'Medium', 'High'];

const PERIODICITY_OPTIONS = [
    'Annual',
    'Weekly',
    'Event based',
    'One time',
    'All time',
    'Half-yearly',
    'Monthly',
    'Quarterly',
    'Renewal',
    'Fortnightly',
    'Daily',
    'Bi-Weekly'
];

const TABLE_COLUMNS = [
    { id: 'company', label: 'Company', minWidth: 150 },
    { id: 'location', label: 'Location', minWidth: 130 },
    { id: 'complianceActivity', label: 'Compliance Activity', minWidth: 200 },
    { id: 'description', label: 'Description', minWidth: 220 },
    { id: 'periodicity', label: 'Periodicity', minWidth: 120 },
    { id: 'dueDate', label: 'Due Date', minWidth: 120 },
    { id: 'owner', label: 'Owner', minWidth: 180 },
    { id: 'manager', label: 'Manager', minWidth: 180 },
    { id: 'risk', label: 'Risk', minWidth: 95 },
    { id: 'sourceUrl', label: 'Source Url', minWidth: 200 }
];

const CHECKBOX_COLUMN_ID = '__checkbox__';
const MIN_COL_WIDTH = 60;
const MAX_COL_WIDTH = 500;
const getInitialColumnWidths = () => ({
    [CHECKBOX_COLUMN_ID]: 52,
    ...TABLE_COLUMNS.reduce((acc, c) => ({ ...acc, [c.id]: c.minWidth }), {})
});

const makeClientId = () =>
    `cid_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const toISODateOnly = (d) => {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const startOfDay = (d) => {
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    return dt;
};

const daysInMonth = (year, monthIndex0) => new Date(year, monthIndex0 + 1, 0).getDate();

/** Normalize Excel/string due date to ISO string (YYYY-MM-DDTHH:mm:ss.sssZ) at UTC midnight so the calendar day does not shift by timezone. */
function normalizeBulkUploadDueDate(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'number') {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (!parsed) return '';
        const utcMidnight = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d));
        return utcMidnight.toISOString();
    }
    const s = String(value).trim();
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, y, m, d] = isoMatch;
        const utcMidnight = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
        return utcMidnight.toISOString();
    }
    const dmyMatch = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (dmyMatch) {
        const [, d, m, y] = dmyMatch;
        const utcMidnight = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
        return utcMidnight.toISOString();
    }
    const parsed = new Date(s);
    if (isNaN(parsed.getTime())) return '';
    const y = parsed.getFullYear();
    const m = parsed.getMonth();
    const d = parsed.getDate();
    const utcMidnight = new Date(Date.UTC(y, m, d));
    return utcMidnight.toISOString();
}

const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const addMonthsPreserveDOM = (date, months) => {
    const d = new Date(date);
    const targetDay = d.getDate();
    const y = d.getFullYear();
    const m = d.getMonth();

    const newMonthIndex = m + months;
    const newYear = y + Math.floor(newMonthIndex / 12);
    const modMonth = ((newMonthIndex % 12) + 12) % 12;

    const dim = daysInMonth(newYear, modMonth);
    const day = Math.min(targetDay, dim);

    return new Date(newYear, modMonth, day);
};

const periodLabelForUI = (period, dueOnDate, customDays) => {
    const d = new Date(dueOnDate);
    const day = d.getDate();
    const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
    const monthShort = d.toLocaleDateString(undefined, { month: 'short' });
    const year = d.getFullYear();

    switch (period) {
        case 'DOES_NOT_REPEAT':
            return 'Does Not Repeat';
        case 'MONTHLY':
            return `Monthly on ${day}th Day`;
        case 'WEEKLY':
            return `Weekly on ${weekday}`;
        case 'QUARTERLY':
            return `Quarterly on ${day}-${monthShort}-${year}`;
        case 'HALF_YEARLY':
            return `Half-Yearly on ${day}-${monthShort}`;
        case 'ANNUAL':
            return `Annually on ${day}-${monthShort}`;
        case 'WORKING_DAY':
            return `Every Day`;
        case 'FORTNIGHTLY':
            return `Fortnightly from ${day}-${monthShort}-${year}`;
        case 'CUSTOM':
            return `Every ${customDays || 1} days`;
        default:
            return '';
    }
};

const computeOccurrences = ({ dueOn, endsOn, period, customEveryDays }) => {
    const start = startOfDay(dueOn);
    const end = startOfDay(endsOn);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    if (start > end) return [];

    const dates = [start];

    const pushWhile = (nextFn) => {
        let cur = start;
        let guard = 0;
        while (guard < 5000) {
            const nxt = nextFn(cur);
            if (nxt > end) break;
            dates.push(startOfDay(nxt));
            cur = nxt;
            guard++;
        }
    };

    switch (period) {
        case 'DOES_NOT_REPEAT':
            return dates;
        case 'WORKING_DAY':
            pushWhile((cur) => addDays(cur, 1));
            return dates;
        case 'WEEKLY':
            pushWhile((cur) => addDays(cur, 7));
            return dates;
        case 'FORTNIGHTLY':
            pushWhile((cur) => addDays(cur, 14));
            return dates;
        case 'CUSTOM': {
            const step = Math.max(1, Number(customEveryDays || 1));
            pushWhile((cur) => addDays(cur, step));
            return dates;
        }
        case 'MONTHLY':
            pushWhile((cur) => addMonthsPreserveDOM(cur, 1));
            return dates;
        case 'QUARTERLY':
            pushWhile((cur) => addMonthsPreserveDOM(cur, 3));
            return dates;
        case 'HALF_YEARLY':
            pushWhile((cur) => addMonthsPreserveDOM(cur, 6));
            return dates;
        case 'ANNUAL':
            pushWhile((cur) => addMonthsPreserveDOM(cur, 12));
            return dates;
        default:
            return dates;
    }
};

const cloneTrackerForDate = (base, dueDateYYYYMMDD, periodicityLabel) => {
    const copy = { ...base };
    delete copy._id;
    copy.clientId = makeClientId();
    copy.status = 'draft';
    copy.dueDate = dueDateYYYYMMDD;
    copy.periodicity = periodicityLabel;
    return copy;
};

const getRowKey = (t) => t._id || t.clientId;

const ensureArray = (v) => (Array.isArray(v) ? v : v === undefined || v === null || v === '' ? [] : [v]);
const joinForRender = (v) => ensureArray(v).map((x) => String(x)).join(', ');
const MULTI_FIELDS = new Set(['category', 'trackerOwner', 'trackerManager']);
const normalizeFieldValue = (field, value) => {
    if (MULTI_FIELDS.has(field)) return ensureArray(value);
    if (field === 'risk') return canonicalRisk(value);
    return value;
};

const InternalTrackers = () => {
    const Router = useNavigate();
    const [auth] = useAuth();

    const [locations, setLocations] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [lawAreas, setLawAreas] = useState([]);
    const [owners, setOwners] = useState([]);
    const [managers, setManagers] = useState([]);
    const [trackers, setTrackers] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [columnVisibility, setColumnVisibility] = useState(() =>
        TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: true }), {})
    );
    const [columnWidths, setColumnWidths] = useState(getInitialColumnWidths);
    const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
    const resizeRef = useRef({ columnId: null, startX: 0, startWidth: 0 });

    const handleResizeStart = useCallback((columnId, e) => {
        e.preventDefault();
        e.stopPropagation();
        const currentWidth = columnWidths[columnId] ?? (columnId === CHECKBOX_COLUMN_ID ? 52 : 150);
        resizeRef.current = { columnId, startX: e.clientX, startWidth: currentWidth };
        const onMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - resizeRef.current.startX;
            const newWidth = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, resizeRef.current.startWidth + delta));
            setColumnWidths((prev) => ({ ...prev, [resizeRef.current.columnId]: newWidth }));
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [columnWidths]);

    const getTableWidth = useCallback(() => {
        const visibleCols = TABLE_COLUMNS.filter((c) => columnVisibility[c.id]);
        return (columnWidths[CHECKBOX_COLUMN_ID] ?? 52) + visibleCols.reduce((sum, c) => sum + (columnWidths[c.id] ?? c.minWidth), 0);
    }, [columnWidths, columnVisibility]);
    const [companyId, setCompanyId] = useState('');
    const [sessionModalOpen, setSessionModalOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [bulkImportOpen, setBulkImportOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isApplyingSchedule, setIsApplyingSchedule] = useState(false);
    const [setDueModalOpen, setSetDueModalOpen] = useState(false);
    const [setDueForm, setSetDueForm] = useState({
        dueOn: '',
        period: 'DOES_NOT_REPEAT',
        customEveryDays: 1,
        endsOn: ''
    });
    const [setDuePreview, setSetDuePreview] = useState('');

    const [prefillData, setPrefillData] = useState({
        companyName: '',
        location: '',
        complianceActivity: '',
        description: '',
        category: [],
        actOrRule: '',
        lawArea: '',
        sectionDesc: '',
        sourceUrl: '',
        periodicity: '',
        dueDate: '',
        internalForm: '',
        trackerOwner: [],
        trackerManager: [],
        risk: 'Low'
    });

    const [selectedRows, setSelectedRows] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [duplicateTrackers, setDuplicateTrackers] = useState([]);
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

    const [massEditData, setMassEditData] = useState({
        companyName: '',
        location: '',
        complianceActivity: '',
        description: '',
        category: [],
        actOrRule: '',
        lawArea: '',
        sectionDesc: '',
        periodicity: '',
        dueDate: '',
        internalForm: '',
        sourceUrl: '',
        trackerOwner: [],
        trackerManager: [],
        risk: ''
    });
    const NONE = "__none__";

    const normalizeNone = (vals) => (Array.isArray(vals) ? vals : []).filter(Boolean);

    const makeExclusiveNone = (vals) => {
        const v = normalizeNone(vals);
        if (v.includes(NONE) && v.length > 1) return [NONE];
        return v;
    };

    const toEmailsOrEmpty = (vals) => (vals.includes(NONE) ? [] : vals.filter((x) => x !== NONE));
    const ownerOptions = owners.map((o) => o.email).filter(Boolean);
    const managerOptions = managers.map((m) => m.email).filter(Boolean);

    const applyExclusiveNone = (prevVals, nextVals) => {
        const prev = normalizeNone(prevVals);
        const next = normalizeNone(nextVals);

        const prevHadNone = prev.includes(NONE);
        const nextHadNone = next.includes(NONE);

        if (!nextHadNone) return next;
        if (!prevHadNone) return [NONE];
        return next.filter((x) => x !== NONE);
    };

    const uiValueFromEmails = (emails) => {
        const v = ensureArray(emails);
        return v.length ? v : [NONE];
    };

    useEffect(() => {
        const fetchUserInfoAndUsers = async () => {
            try {
                const { data } = await api.get('detail/userinfo?view=meta');
                setCompanyId(data.companyDetails._id);
                setCompanies([data.companyDetails.company_name]);
                setLocations(data.companyDetails.locations.flat());
            } catch (error) {
                if (error.response?.status === 401) {
                    setSessionModalOpen(true);
                }
                console.error('Error fetching user info:', error);
            }
        };

        fetchUserInfoAndUsers();
    }, []);

    useEffect(() => {
        const CompanyInfoandRoles = async () => {
            if (!companyId) return;
            try {
                const response = await api.post('company-detail/companyinfo', { id: companyId });
                const { managers, owners } = response.data.json;
                setManagers(managers);
                setOwners(owners);

                setPrefillData((prevData) => ({
                    ...prevData,
                    trackerOwner: owners[0] ? [owners[0].email] : [],
                    trackerManager: managers[0] ? [managers[0].email] : []
                }));
            } catch (error) {
                console.error('Error fetching company info:', error);
            }
        };

        const fetchLawAreas = async () => {
            try {
                const response = await api.get('tracker/law-area');
                if (response.data?.lawareas) {
                    setLawAreas(response.data.lawareas.map((la) => la.law_area));
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    setSessionModalOpen(true);
                }
                console.error('Error fetching law areas:', error);
            }
        };

        CompanyInfoandRoles();
        fetchLawAreas();
    }, [companyId]);

    useEffect(() => {
        if (!setDueForm.dueOn) {
            setSetDuePreview('');
            return;
        }
        setSetDuePreview(periodLabelForUI(setDueForm.period, setDueForm.dueOn, setDueForm.customEveryDays));
    }, [setDueForm.dueOn, setDueForm.period, setDueForm.customEveryDays]);

    const hasOwnerOrManager = (t) => {
        const owners = ensureArray(t.trackerOwner).filter((x) => x && x !== '__none__');
        const managers = ensureArray(t.trackerManager).filter((x) => x && x !== '__none__');
        return owners.length > 0 || managers.length > 0;
    };

    const handleSaveAllTrackers = async () => {
        try {
            setIsSaving(true);
            const toCreate = trackers.filter((t) => !t._id || t.status === 'draft');
            const toUpdate = trackers.filter((t) => t._id && t.status !== 'draft');

            const invalid = toCreate.filter((t) => !hasOwnerOrManager(t));
            if (invalid.length > 0) {
                toast.error(`At least one of Owner or Manager is required. ${invalid.length} tracker(s) are missing both.`);
                return;
            }

            let createdIds = [];
            let showedDuplicateWarning = false;

            if (toCreate.length > 0) {
                const res = await api.post('tracker/create-tracker', { trackers: toCreate });
                createdIds = res.data?.insertedIds || [];
                toCreate.forEach((t, i) => {
                    if (createdIds[i]) {
                        t._id = createdIds[i];
                        t.status = '';
                    }
                });
                const skipped = res.data?.skippedCount ?? 0;
                const dupList = res.data?.duplicateTrackers ?? [];
                if (skipped > 0 && dupList.length) {
                    showedDuplicateWarning = true;
                    setDuplicateTrackers(dupList);
                    setDuplicateModalOpen(true);
                    const created = res.data?.createdCount ?? createdIds.length;
                    toast.warning(
                        created > 0
                            ? `Created ${created} tracker(s). ${skipped} duplicate(s) skipped – view details below.`
                            : `No trackers created – all ${skipped} row(s) were duplicates. View details below.`,
                        { autoClose: 6000 }
                    );
                } else {
                    toast.success('Trackers saved successfully!');
                }
            } else {
                toast.success('Trackers saved successfully!');
            }

            for (const tracker of toUpdate) {
                await api.put('tracker/update-tracker', tracker);
            }

            if (toCreate.length === 0 || createdIds.length === toCreate.length) {
                setTrackers([]);
                setSelectedRows([]);
            } else if (!showedDuplicateWarning) {
                toast.warning('Some trackers were not created. Please review skipped rows.');
            }
        } catch (error) {
            console.error('Error saving trackers:', error);
            toast.error('Failed to save trackers.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChangeModal = (e) => {
        const { name, value } = e.target;
        setPrefillData((prev) => ({ ...prev, [name]: normalizeFieldValue(name, value) }));
    };

    const handleTrackerFieldChange = (trackerIndex, field, value) => {
        setTrackers((prev) => {
            const updated = [...prev];
            updated[trackerIndex] = { ...updated[trackerIndex], [field]: normalizeFieldValue(field, value) };
            return updated;
        });
    };

    const handleRowSelect = (trackerKey) => {
        setSelectedRows((prevSelected) => {
            if (prevSelected.includes(trackerKey)) {
                return prevSelected.filter((id) => id !== trackerKey);
            } else {
                return [...prevSelected, trackerKey];
            }
        });
    };

    const isRowSelected = (trackerKey) => selectedRows.includes(trackerKey);

    const pageTrackers = trackers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const pageKeys = pageTrackers.map((t) => getRowKey(t));
    const allOnPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selectedRows.includes(k));
    const someOnPageSelected = pageKeys.some((k) => selectedRows.includes(k));

    const handleSelectAllOnPage = () => {
        if (allOnPageSelected) {
            setSelectedRows((prev) => prev.filter((id) => !pageKeys.includes(id)));
        } else {
            setSelectedRows((prev) => [...new Set([...prev, ...pageKeys])]);
        }
    };

    const handleMassFieldChange = (field, value) => {
        setTrackers((prev) =>
            prev.map((tr) => (selectedRows.includes(getRowKey(tr)) ? { ...tr, [field]: normalizeFieldValue(field, value) } : tr))
        );
    };

    const handleAddTracker = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    const handleSubmitNewTracker = (e) => {
        e.preventDefault();

        const ownerEmails = ensureArray(prefillData.trackerOwner);
        const managerEmails = ensureArray(prefillData.trackerManager);

        if (ownerEmails.length === 0 && managerEmails.length === 0) {
            toast.error("At least one of Owner or Manager is required.");
            return;
        }

        setTrackers((prevTrackers) => [
            ...prevTrackers,
            { ...prefillData, clientId: makeClientId(), status: 'draft' }
        ]);

        toast.info("Tracker added as draft. Please review and click 'Save Trackers' to finalize.");

        setPrefillData({
            companyName: '',
            location: '',
            complianceActivity: '',
            description: '',
            category: [],
            actOrRule: '',
            lawArea: '',
            sectionDesc: '',
            periodicity: '',
            sourceUrl: '',
            dueDate: '',
            internalForm: '',
            trackerOwner: [],
            trackerManager: [],
            risk: 'Low'
        });

        setOpenModal(false);
    };


    const handleBulkImport = () => setBulkImportOpen(true);
    const handleCloseBulkImport = () => setBulkImportOpen(false);

    const handleFileChange = (e) => {
        if (e.target.files?.[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) {
            toast.error('No file selected. Please choose a file to upload.');
            return;
        }
        if (!(selectedFile instanceof Blob)) {
            toast.error('Invalid file type. Please upload a valid .xlsx file.');
            return;
        }

        try {
            setIsUploading(true);
            const reader = new FileReader();

            reader.onload = async (event) => {
                try {
                    const data = new Uint8Array(event.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                    if (rows.length < 2) {
                        toast.error('The uploaded file is empty or missing data rows.');
                        return;
                    }

                    const headers = rows[0].map((hdr) => {
                        return (hdr || '').trim().toLowerCase().replace(/\s+/g, '');
                    });

                    const renameKeysMap = {
                        companyname: 'companyName',
                        location: 'location',
                        complianceactivity: 'complianceActivity',
                        description: 'description',
                        category: 'category',
                        actorrule: 'actOrRule',
                        lawarea: 'lawArea',
                        sectiondesc: 'sectionDesc',
                        periodicity: 'periodicity',
                        duedate: 'dueDate',
                        form: 'internalForm',
                        trackerowner: 'trackerOwner',
                        sourceurl: 'sourceUrl',
                        trackermanager: 'trackerManager',
                        risk: 'risk'
                    };

                    const trackersToDraft = [];
                    for (let i = 1; i < rows.length; i++) {
                        const row = rows[i];
                        if (!row || row.length === 0) continue;

                        const rowData = {};

                        headers.forEach((header, colIndex) => {
                            const cellValue = row[colIndex] || '';
                            if (header in renameKeysMap) {
                                const newKey = renameKeysMap[header];
                                rowData[newKey] = cellValue;
                            }
                        });

                        const userCompany = companies && companies[0] ? String(companies[0]).trim() : '';
                        const rowCompany = rowData.companyName != null ? String(rowData.companyName).trim() : '';
                        if (!rowCompany) {
                            rowData.companyName = userCompany || rowData.companyName || '';
                        } else if (userCompany && rowCompany !== userCompany) {
                            toast.error(`Row ${i + 2}: Company "${rowCompany}" does not match your company ("${userCompany}"). Use your company name or leave Company blank to auto-fill.`);
                            setIsUploading(false);
                            return;
                        }

                        if (rowData.dueDate) {
                            rowData.dueDate = normalizeBulkUploadDueDate(rowData.dueDate) || '';
                        }

                        if (typeof rowData.category === 'string') {
                            rowData.category = rowData.category
                                .split(/[;,]/)
                                .map((x) => x.trim())
                                .filter(Boolean);
                        }
                        if (!Array.isArray(rowData.category)) rowData.category = [];

                        if (typeof rowData.trackerOwner === 'string') {
                            rowData.trackerOwner = rowData.trackerOwner
                                .split(/[;,]/)
                                .map((x) => x.trim())
                                .filter(Boolean);
                        }
                        if (!Array.isArray(rowData.trackerOwner)) rowData.trackerOwner = [];

                        if (typeof rowData.trackerManager === 'string') {
                            rowData.trackerManager = rowData.trackerManager
                                .split(/[;,]/)
                                .map((x) => x.trim())
                                .filter(Boolean);
                        }
                        if (!Array.isArray(rowData.trackerManager)) rowData.trackerManager = [];
                        rowData.risk = canonicalRisk(rowData.risk);

                        rowData.clientId = makeClientId();
                        rowData.status = 'draft';

                        trackersToDraft.push(rowData);
                    }

                    setTrackers((prevTrackers) => [...prevTrackers, ...trackersToDraft]);
                    toast.success('File parsed successfully. Trackers added as DRAFT.');
                } catch (error) {
                    console.error('Error processing Excel file:', error);
                    toast.error(`File processing error: ${error.message}`);
                } finally {
                    setIsUploading(false);
                }
            };

            reader.onerror = () => {
                toast.error('Failed to read the file. Please try again.');
                setIsUploading(false);
            };

            reader.readAsArrayBuffer(selectedFile);
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('An unexpected error occurred. Please try again.');
            setIsUploading(false);
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSessionModalClose = () => {
        setSessionModalOpen(false);
        Router('/login');
    };

    const handleMassEditChange = (e) => {
        const { name, value } = e.target;
        setMassEditData((prev) => ({ ...prev, [name]: normalizeFieldValue(name, value) }));
    };

    const handleApplyMassEdit = () => {
        Object.keys(massEditData).forEach((field) => {
            if (massEditData[field] !== '') {
                handleMassFieldChange(field, massEditData[field]);
            }
        });
        toast.success('Mass edit applied successfully.');
        setMassEditData({
            companyName: '',
            location: '',
            complianceActivity: '',
            description: '',
            category: [],
            actOrRule: '',
            lawArea: '',
            sectionDesc: '',
            periodicity: '',
            dueDate: '',
            internalForm: '',
            sourceUrl: '',
            trackerOwner: [],
            trackerManager: [],
            risk: ''
        });
    };

    const openSetDueModal = () => {
        const today = toISODateOnly(new Date());
        setSetDueForm((p) => ({
            ...p,
            dueOn: p.dueOn || today,
            endsOn: p.endsOn || today
        }));
        setSetDueModalOpen(true);
    };

    const closeSetDueModal = () => setSetDueModalOpen(false);

    const handleApplySetDueDate = async () => {
        if (isApplyingSchedule) return;

        const { dueOn, endsOn, period, customEveryDays } = setDueForm;

        if (!dueOn || !endsOn) {
            toast.error("Please select both Due on and Ends on dates.");
            return;
        }

        const selectedSet = new Set(selectedRows);
        const selectedTrackers = trackers.filter((t) => selectedSet.has(getRowKey(t)));

        if (!selectedTrackers.length) {
            toast.error("No trackers selected.");
            return;
        }

        const missingOwnerManager = selectedTrackers.filter((t) => !hasOwnerOrManager(t));
        if (missingOwnerManager.length > 0) {
            toast.error(`At least one of Owner or Manager is required. ${missingOwnerManager.length} selected tracker(s) have both empty.`);
            return;
        }

        try {
            setIsApplyingSchedule(true);

            const payload = {
                trackers: selectedTrackers.map((t) => ({
                    location: t.location,
                    complianceActivity: t.complianceActivity,
                    description: t.description,
                    category: ensureArray(t.category),
                    actOrRule: t.actOrRule,
                    lawArea: t.lawArea,
                    trackerOwner: ensureArray(t.trackerOwner),
                    trackerManager: ensureArray(t.trackerManager),
                    risk: t.risk,
                    sourceUrl: t.sourceUrl,
                    internalForm: t.internalForm,
                    assignedTo: ensureArray(t.assignedTo).length ? ensureArray(t.assignedTo) : ["N/A"],
                    proof: t.proof || false,
                })),
                schedule: {
                    period,
                    startOn: dueOn,
                    endsOn,
                    customEveryDays: period === "CUSTOM" ? Number(customEveryDays || 1) : undefined,
                },
            };

            const res = await api.post("/tracker/create-tracker", payload);

            const skipped = res?.data?.skippedCount ?? 0;
            const dupList = res?.data?.duplicateTrackers ?? [];
            if (skipped > 0 && dupList.length) {
                setDuplicateTrackers(dupList);
                setDuplicateModalOpen(true);
                const created = res?.data?.createdCount ?? 0;
                toast.warning(
                    created > 0
                        ? `Created ${created} tracker(s). ${skipped} duplicate(s) skipped – view details below.`
                        : `No trackers created – all ${skipped} row(s) were duplicates. View details below.`,
                    { autoClose: 6000 }
                );
            } else {
                toast.success(res?.data?.message || "Trackers created successfully.");
            }
            setSelectedRows([]);
            closeSetDueModal();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to create trackers.");
        } finally {
            setIsApplyingSchedule(false);
        }
    };

    const downloadDuplicateCsv = () => {
        if (!duplicateTrackers.length) return;
        const escapeCsv = (v) => {
            const s = String(v ?? "").replace(/"/g, '""');
            return /[",\n\r]/.test(s) ? `"${s}"` : s;
        };
        const headers = ["Location", "Compliance Activity", "Due Date", "Description", "Category", "Act/Rule", "Law Area", "Form", "Consequence", "Risk"];
        const keys = ["location", "complianceActivity", "dueDate", "description", "category", "actOrRule", "lawArea", "form", "consequence", "risk"];
        const headerRow = headers.join(",");
        const dataRows = duplicateTrackers.map((row) => keys.map((k) => escapeCsv(row[k])).join(","));
        const csv = [headerRow, ...dataRows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `duplicate-trackers-${toISODateOnly(new Date())}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const closeDuplicateModal = () => {
        setDuplicateModalOpen(false);
        setDuplicateTrackers([]);
    };

    return (
        <Layout title="Internal Tracker">
            <Modal open={sessionModalOpen} onClose={handleSessionModalClose}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2, color: 'red' }}>
                        Session Expired
                    </Typography>
                    <Typography sx={{ mb: 2 }}>Your session has expired. Please log in again.</Typography>
                    <Button variant="contained" onClick={handleSessionModalClose}>
                        Go to Login
                    </Button>
                </Box>
            </Modal>

            <Modal open={duplicateModalOpen} onClose={closeDuplicateModal}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 900,
                        maxHeight: '85vh',
                        bgcolor: 'background.paper',
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 1, color: 'warning.main' }}>
                        Skipped duplicates ({duplicateTrackers.length} row(s))
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These trackers already exist and were not created. Download the list to update your sheet and re-upload if needed.
                    </Typography>
                    <TableContainer sx={{ flex: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Compliance Activity</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Act/Rule</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Law Area</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Risk</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {duplicateTrackers.map((row, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{row.location}</TableCell>
                                        <TableCell>{row.complianceActivity}</TableCell>
                                        <TableCell>{row.dueDate}</TableCell>
                                        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.description}</TableCell>
                                        <TableCell>{row.category}</TableCell>
                                        <TableCell>{row.actOrRule}</TableCell>
                                        <TableCell>{row.lawArea}</TableCell>
                                        <TableCell>{row.risk}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" onClick={downloadDuplicateCsv}>
                            Download as CSV
                        </Button>
                        <Button variant="contained" onClick={closeDuplicateModal}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={bulkImportOpen} onClose={handleCloseBulkImport}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        p: 4,
                        borderRadius: 3,
                        textAlign: 'center',
                        border: '2px solid #1976d2'
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Bulk Import Trackers
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Download the sample template and upload after filling.
                    </Typography>
                    <a href="/sample-import.xlsx" download style={{ textDecoration: 'none', color: 'blue' }}>
                        Download Sample Template
                    </a>
                    <Box sx={{ mt: 2 }}>
                        <input type="file" accept=".xlsx" onChange={handleFileChange} />
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        {isUploading ? (
                            <CircularProgress size={24} />
                        ) : (
                            <Button variant="contained" onClick={handleFileUpload} disabled={!selectedFile}>
                                Upload
                            </Button>
                        )}
                        <Button variant="outlined" onClick={handleCloseBulkImport}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={openModal} onClose={handleCloseModal}>
                <Box
                    component="form"
                    onSubmit={handleSubmitNewTracker}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 500,
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 4,
                        overflowY: 'auto',
                        maxHeight: '90vh',
                        border: '2px solid #1976d2'
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Add Tracker
                    </Typography>

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Company Name</InputLabel>
                        <Select
                            name="companyName"
                            value={prefillData.companyName}
                            onChange={handleInputChangeModal}
                            label="Company Name"
                        >
                            {companies.map((company, idx) => (
                                <MenuItem key={idx} value={company}>
                                    {company}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Location</InputLabel>
                        <Select
                            name="location"
                            value={prefillData.location}
                            onChange={handleInputChangeModal}
                            label="Location"
                        >
                            {locations.map((loc, idx) => (
                                <MenuItem key={idx} value={loc}>
                                    {loc}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Compliance Activity"
                        name="complianceActivity"
                        fullWidth
                        margin="normal"
                        value={prefillData.complianceActivity}
                        onChange={handleInputChangeModal}
                    />
                    <TextField
                        label="Description"
                        name="description"
                        fullWidth
                        margin="normal"
                        value={prefillData.description}
                        onChange={handleInputChangeModal}
                    />

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Category</InputLabel>
                        <Select
                            name="category"
                            value={prefillData.category || ""}
                            onChange={handleInputChangeModal}
                            label="Category"
                        >
                            {CATEGORY_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>


                    <TextField
                        label="Act or Rule"
                        name="actOrRule"
                        fullWidth
                        margin="normal"
                        value={prefillData.actOrRule}
                        onChange={handleInputChangeModal}
                    />

                    <FormControl fullWidth size="small" margin="none">
                        <InputLabel id="law-area-label">Law Area</InputLabel>
                        <Select
                            labelId="law-area-label"
                            name="lawArea"
                            value={prefillData.lawArea}
                            onChange={handleInputChangeModal}
                            label="Law Area"
                        >
                            {lawAreas.map((la, i) => (
                                <MenuItem key={i} value={la}>
                                    {la}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>


                    <TextField
                        label="Section Description"
                        name="sectionDesc"
                        fullWidth
                        margin="normal"
                        value={prefillData.sectionDesc}
                        onChange={handleInputChangeModal}
                    />

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Periodicity</InputLabel>
                        <Select
                            name="periodicity"
                            value={prefillData.periodicity}
                            onChange={handleInputChangeModal}
                            label="Periodicity"
                        >
                            {PERIODICITY_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Due Date"
                        name="dueDate"
                        type="date"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={prefillData.dueDate}
                        onChange={handleInputChangeModal}
                    />
                    <TextField
                        label="Internal Form"
                        name="internalForm"
                        fullWidth
                        margin="normal"
                        value={prefillData.internalForm}
                        onChange={handleInputChangeModal}
                    />
                    <TextField
                        label="Source Url"
                        name="sourceUrl"
                        fullWidth
                        margin="normal"
                        value={prefillData.sourceUrl}
                        onChange={handleInputChangeModal}
                    />

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Owner</InputLabel>
                        <Select
                            multiple
                            label="Owner"
                            value={uiValueFromEmails(prefillData.trackerOwner)}
                            onChange={(e) => {
                                const nextUi = applyExclusiveNone(
                                    uiValueFromEmails(prefillData.trackerOwner),
                                    e.target.value
                                );
                                setPrefillData((p) => ({ ...p, trackerOwner: toEmailsOrEmpty(nextUi) }));
                            }}
                            renderValue={(selected) => (selected.includes(NONE) ? "None" : selected.join(", "))}
                        >
                            <MenuItem value={NONE}>None</MenuItem>
                            {ownerOptions.map((email) => (
                                <MenuItem key={email} value={email}>
                                    {email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>


                    <FormControl fullWidth margin="normal">
                        <InputLabel>Manager</InputLabel>
                        <Select
                            multiple
                            label="Manager"
                            value={uiValueFromEmails(prefillData.trackerManager)}
                            onChange={(e) => {
                                const nextUi = applyExclusiveNone(
                                    uiValueFromEmails(prefillData.trackerManager),
                                    e.target.value
                                );
                                setPrefillData((p) => ({ ...p, trackerManager: toEmailsOrEmpty(nextUi) }));
                            }}
                            renderValue={(selected) => (selected.includes(NONE) ? "None" : selected.join(", "))}
                        >
                            <MenuItem value={NONE}>None</MenuItem>
                            {managerOptions.map((email) => (
                                <MenuItem key={email} value={email}>
                                    {email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>


                    <FormControl fullWidth margin="normal">
                        <InputLabel>Risk</InputLabel>
                        <Select name="risk" value={prefillData.risk} onChange={handleInputChangeModal} label="Risk">
                            {RISK_OPTIONS.map((risk) => <MenuItem key={risk} value={risk}>{risk}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button variant="contained" color="primary" type="submit">
                            Save
                        </Button>
                        <Button variant="outlined" color="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={setDueModalOpen} onClose={closeSetDueModal}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 520,
                        bgcolor: 'background.paper',
                        p: 4,
                        borderRadius: 3,
                        border: '2px solid #1976d2'
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Set Due Date for {selectedRows.length} tracker(s)
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Due on"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={setDueForm.dueOn}
                                onChange={(e) => setSetDueForm((p) => ({ ...p, dueOn: e.target.value }))}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Period</InputLabel>
                                <Select
                                    label="Period"
                                    value={setDueForm.period}
                                    onChange={(e) => setSetDueForm((p) => ({ ...p, period: e.target.value }))}
                                >
                                    <MenuItem value="DOES_NOT_REPEAT">Does Not Repeat (Single time creation)</MenuItem>
                                    <MenuItem value="MONTHLY">Monthly (Every Month)</MenuItem>
                                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                                    <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                                    <MenuItem value="HALF_YEARLY">Half-Yearly</MenuItem>
                                    <MenuItem value="ANNUAL">Annually</MenuItem>
                                    <MenuItem value="WORKING_DAY">Working Day (Every Day)</MenuItem>
                                    <MenuItem value="FORTNIGHTLY">Fortnightly</MenuItem>
                                    <MenuItem value="CUSTOM">Custom (Every N days)</MenuItem>
                                </Select>
                            </FormControl>
                            {setDuePreview ? (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Preview: {setDuePreview}
                                </Typography>
                            ) : null}
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Custom frequency (days)"
                                type="number"
                                fullWidth
                                disabled={setDueForm.period !== 'CUSTOM'}
                                inputProps={{ min: 1 }}
                                value={setDueForm.customEveryDays}
                                onChange={(e) =>
                                    setSetDueForm((p) => ({ ...p, customEveryDays: Number(e.target.value || 1) }))
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Ends on"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={setDueForm.endsOn}
                                onChange={(e) => setSetDueForm((p) => ({ ...p, endsOn: e.target.value }))}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button variant="outlined" onClick={closeSetDueModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleApplySetDueDate}
                            disabled={isApplyingSchedule}
                            startIcon={isApplyingSchedule ? <CircularProgress size={18} /> : null}
                        >
                            {isApplyingSchedule ? "Applying..." : "Apply"}
                        </Button>

                    </Box>
                </Box>
            </Modal>

            <Box sx={{ p: 4, backgroundColor: '#e3f2fd', minHeight: '100vh' }}>
                <Container maxWidth="lg">
                    <Paper sx={{ p: 3, mb: 2, borderRadius: 2, boxShadow: 3 }}>
                        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <Typography variant="h5">Internal Trackers</Typography>
                            </Grid>
                            <Grid item>
                                <Button variant="contained" sx={{ mr: 1 }} onClick={handleAddTracker}>
                                    + Add Tracker
                                </Button>
                                <Button variant="outlined" onClick={handleBulkImport} sx={{ mr: 1 }}>
                                    Bulk Import
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={selectedRows.length === 0}
                                    onClick={openSetDueModal}
                                >
                                    Set Due Date
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
                        {trackers && trackers.length > 0 ? (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Tooltip title="Show / hide columns">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                                            sx={{ border: '1px solid #ccc' }}
                                        >
                                            <ViewColumnIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="caption" color="text.secondary">
                                        Toggle columns
                                    </Typography>
                                </Box>
                                <Menu
                                    anchorEl={columnsMenuAnchor}
                                    open={Boolean(columnsMenuAnchor)}
                                    onClose={() => setColumnsMenuAnchor(null)}
                                    PaperProps={{ sx: { maxHeight: 400 } }}
                                >
                                    {TABLE_COLUMNS.map((col) => (
                                        <MenuItem
                                            key={col.id}
                                            onClick={() => setColumnVisibility((prev) => ({ ...prev, [col.id]: !prev[col.id] }))}
                                        >
                                            <Checkbox checked={columnVisibility[col.id]} size="small" sx={{ mr: 1 }} />
                                            {col.label}
                                        </MenuItem>
                                    ))}
                                </Menu>
                                <TableContainer sx={{ width: '100%', maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
                                    <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: getTableWidth(), width: getTableWidth() }}>
                                        <colgroup>
                                            <col style={{ width: columnWidths[CHECKBOX_COLUMN_ID] ?? 52 }} />
                                            {TABLE_COLUMNS.filter((c) => columnVisibility[c.id]).map((col) => (
                                                <col key={col.id} style={{ width: columnWidths[col.id] ?? col.minWidth }} />
                                            ))}
                                        </colgroup>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox" sx={{ border: '1px solid #ccc', backgroundColor: '#e0f7fa', position: 'sticky', left: 0, zIndex: 3, width: columnWidths[CHECKBOX_COLUMN_ID] ?? 52, minWidth: MIN_COL_WIDTH, maxWidth: MAX_COL_WIDTH, boxSizing: 'border-box', boxShadow: '2px 0 4px -2px rgba(0,0,0,0.15)' }}>
                                                    <Checkbox
                                                        indeterminate={someOnPageSelected && !allOnPageSelected}
                                                        checked={allOnPageSelected}
                                                        onChange={handleSelectAllOnPage}
                                                        disabled={pageKeys.length === 0}
                                                    />
                                                    <Box
                                                        component="span"
                                                        onMouseDown={(e) => handleResizeStart(CHECKBOX_COLUMN_ID, e)}
                                                        sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', borderRight: '2px solid transparent', '&:hover': { borderRightColor: 'primary.main' } }}
                                                    />
                                                </TableCell>
                                                {TABLE_COLUMNS.filter((c) => columnVisibility[c.id]).map((col) => {
                                                    const w = columnWidths[col.id] ?? col.minWidth;
                                                    return (
                                                        <TableCell key={col.id} sx={{ border: '1px solid #ccc', backgroundColor: '#e0f7fa', fontWeight: 'bold', width: w, minWidth: MIN_COL_WIDTH, maxWidth: MAX_COL_WIDTH, boxSizing: 'border-box', overflow: 'hidden', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', position: 'relative' }}>
                                                            {col.label}
                                                            <Box
                                                                component="span"
                                                                onMouseDown={(e) => handleResizeStart(col.id, e)}
                                                                sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', borderRight: '2px solid transparent', '&:hover': { borderRightColor: 'primary.main' } }}
                                                            />
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {trackers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((tracker, index) => {
                                                const globalIndex = page * rowsPerPage + index;
                                                const rowKey = getRowKey(tracker);

                                                const dueDateValue =
                                                    tracker.dueDate
                                                        ? (String(tracker.dueDate).includes('T')
                                                            ? String(tracker.dueDate).split('T')[0]
                                                            : String(tracker.dueDate))
                                                        : '';

                                                const baseCellSx = { border: '1px solid #ccc', boxSizing: 'border-box', overflow: 'hidden', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' };
                                                const renderCell = (colId) => {
                                                    const colDef = TABLE_COLUMNS.find((c) => c.id === colId);
                                                    const w = columnWidths[colId] ?? colDef?.minWidth;
                                                    const cellSx = { ...baseCellSx, width: w, minWidth: MIN_COL_WIDTH, maxWidth: MAX_COL_WIDTH };
                                                    switch (colId) {
                                                        case 'company':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <FormControl variant="standard" fullWidth>
                                                                        <InputLabel>Company</InputLabel>
                                                                        <Select
                                                                            value={tracker.companyName || ''}
                                                                            onChange={(e) => handleTrackerFieldChange(globalIndex, 'companyName', e.target.value)}
                                                                        >
                                                                            {companies.map((company, idx) => (
                                                                                <MenuItem key={idx} value={company}>{company}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                    {tracker.status === 'draft' && (
                                                                        <Chip label="Draft" size="small" color="warning" sx={{ ml: 1 }} />
                                                                    )}
                                                                </TableCell>
                                                            );
                                                        case 'location':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <FormControl variant="standard" fullWidth>
                                                                        <InputLabel>Location</InputLabel>
                                                                        <Select
                                                                            value={tracker.location || ''}
                                                                            onChange={(e) => handleTrackerFieldChange(globalIndex, 'location', e.target.value)}
                                                                        >
                                                                            {locations.map((loc, idx) => (
                                                                                <MenuItem key={idx} value={loc}>{loc}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                </TableCell>
                                                            );
                                                        case 'complianceActivity':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <TextField
                                                                        variant="standard"
                                                                        value={tracker.complianceActivity || ''}
                                                                        onChange={(e) => handleTrackerFieldChange(globalIndex, 'complianceActivity', e.target.value)}
                                                                    />
                                                                </TableCell>
                                                            );
                                                        case 'description':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <TextField
                                                                        variant="standard"
                                                                        value={tracker.description || ''}
                                                                        onChange={(e) => handleTrackerFieldChange(globalIndex, 'description', e.target.value)}
                                                                    />
                                                                </TableCell>
                                                            );
                                                        case 'periodicity':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <Select
                                                                        variant="standard"
                                                                        value={tracker.periodicity || ''}
                                                                        onChange={(e) => handleTrackerFieldChange(globalIndex, 'periodicity', e.target.value)}
                                                                    >
                                                                        {PERIODICITY_OPTIONS.map((opt) => (
                                                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </TableCell>
                                                            );
                                                        case 'dueDate':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <TextField
                                                                        type="date"
                                                                        variant="standard"
                                                                        value={dueDateValue}
                                                                        onChange={(e) => handleTrackerFieldChange(globalIndex, 'dueDate', e.target.value)}
                                                                    />
                                                                </TableCell>
                                                            );
                                                        case 'owner':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <Select
                                                                        variant="standard"
                                                                        multiple
                                                                        value={ensureArray(tracker.trackerOwner)}
                                                                        onChange={(e) => handleTrackerFieldChange(globalIndex, 'trackerOwner', e.target.value)}
                                                                        renderValue={(sel) => joinForRender(sel)}
                                                                    >
                                                                        {owners.map((o) => (
                                                                            <MenuItem key={o.email} value={o.email}>{o.email}</MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </TableCell>
                                                            );
                                                        case 'manager':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <Select
                                                                        variant="standard"
                                                                        multiple
                                                                        value={ensureArray(tracker.trackerManager)}
                                                                        onChange={(e) => handleTrackerFieldChange(globalIndex, 'trackerManager', e.target.value)}
                                                                        renderValue={(sel) => joinForRender(sel)}
                                                                    >
                                                                        {managers.map((m) => (
                                                                            <MenuItem key={m.email} value={m.email}>{m.email}</MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </TableCell>
                                                            );
                                                        case 'risk':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <Select
                                                                        variant="standard"
                                                                        value={tracker.risk || 'Low'}
                                                                        onChange={(e) => handleTrackerFieldChange(globalIndex, 'risk', e.target.value)}
                                                                    >
                                                                        {RISK_OPTIONS.map((risk) => <MenuItem key={risk} value={risk}>{risk}</MenuItem>)}
                                                                    </Select>
                                                                </TableCell>
                                                            );
                                                        case 'sourceUrl':
                                                            return (
                                                                <TableCell key={colId} sx={cellSx}>
                                                                    <TextField
                                                                        variant="standard"
                                                                        value={tracker.sourceUrl || ''}
                                                                        onChange={(e) => handleTrackerFieldChange(globalIndex, 'sourceUrl', e.target.value)}
                                                                    />
                                                                </TableCell>
                                                            );
                                                        default:
                                                            return null;
                                                    }
                                                };

                                                return (
                                                    <TableRow key={rowKey || globalIndex} hover>
                                                        <TableCell padding="checkbox" sx={{ border: '1px solid #ccc', position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'background.paper', width: columnWidths[CHECKBOX_COLUMN_ID] ?? 52, minWidth: MIN_COL_WIDTH, maxWidth: MAX_COL_WIDTH, boxSizing: 'border-box', boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)' }}>
                                                            <Checkbox
                                                                checked={isRowSelected(rowKey)}
                                                                onChange={() => handleRowSelect(rowKey)}
                                                            />
                                                        </TableCell>
                                                        {TABLE_COLUMNS.filter((c) => columnVisibility[c.id]).map((col) => renderCell(col.id))}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={trackers.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />

                                {selectedRows.length > 0 && (
                                    <Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                                        <Typography variant="body2" gutterBottom>
                                            Change fields for {selectedRows.length} selected trackers:
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormControl variant="standard" fullWidth>
                                                    <InputLabel>Company Name</InputLabel>
                                                    <Select name="companyName" value={massEditData.companyName} onChange={handleMassEditChange}>
                                                        {companies.map((company, idx) => (
                                                            <MenuItem key={idx} value={company}>
                                                                {company}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormControl variant="standard" fullWidth>
                                                    <InputLabel>Location</InputLabel>
                                                    <Select name="location" value={massEditData.location} onChange={handleMassEditChange}>
                                                        {locations.map((loc, idx) => (
                                                            <MenuItem key={idx} value={loc}>
                                                                {loc}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Compliance Activity"
                                                    name="complianceActivity"
                                                    variant="standard"
                                                    value={massEditData.complianceActivity}
                                                    onChange={handleMassEditChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Description"
                                                    name="description"
                                                    variant="standard"
                                                    value={massEditData.description}
                                                    onChange={handleMassEditChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormControl variant="standard" fullWidth>
                                                    <InputLabel>Category</InputLabel>
                                                    <Select
                                                        multiple
                                                        name="category"
                                                        value={ensureArray(massEditData.category)}
                                                        onChange={handleMassEditChange}
                                                        renderValue={(sel) => joinForRender(sel)}
                                                    >
                                                        {CATEGORY_OPTIONS.map((opt) => (
                                                            <MenuItem key={opt} value={opt}>
                                                                {opt}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Act or Rule"
                                                    name="actOrRule"
                                                    variant="standard"
                                                    value={massEditData.actOrRule}
                                                    onChange={handleMassEditChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Law Area"
                                                    name="lawArea"
                                                    variant="standard"
                                                    value={massEditData.lawArea}
                                                    onChange={handleMassEditChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Section Description"
                                                    name="sectionDesc"
                                                    variant="standard"
                                                    value={massEditData.sectionDesc}
                                                    onChange={handleMassEditChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormControl variant="standard" fullWidth>
                                                    <InputLabel>Periodicity</InputLabel>
                                                    <Select
                                                        name="periodicity"
                                                        value={massEditData.periodicity}
                                                        onChange={handleMassEditChange}
                                                    >
                                                        {PERIODICITY_OPTIONS.map((opt) => (
                                                            <MenuItem key={opt} value={opt}>
                                                                {opt}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Due Date"
                                                    name="dueDate"
                                                    type="date"
                                                    InputLabelProps={{ shrink: true }}
                                                    variant="standard"
                                                    value={massEditData.dueDate}
                                                    onChange={handleMassEditChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Internal Form"
                                                    name="internalForm"
                                                    variant="standard"
                                                    value={massEditData.internalForm}
                                                    onChange={handleMassEditChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormControl variant="standard" fullWidth>
                                                    <InputLabel>Tracker Owner</InputLabel>
                                                    <Select
                                                        multiple
                                                        name="trackerOwner"
                                                        value={ensureArray(massEditData.trackerOwner)}
                                                        onChange={handleMassEditChange}
                                                        renderValue={(sel) => joinForRender(sel)}
                                                    >
                                                        {owners.map((owner, idx) => (
                                                            <MenuItem key={idx} value={owner.email}>
                                                                {owner.email}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormControl variant="standard" fullWidth>
                                                    <InputLabel>Tracker Manager</InputLabel>
                                                    <Select
                                                        multiple
                                                        name="trackerManager"
                                                        value={ensureArray(massEditData.trackerManager)}
                                                        onChange={handleMassEditChange}
                                                        renderValue={(sel) => joinForRender(sel)}
                                                    >
                                                        {managers.map((m, idx) => (
                                                            <MenuItem key={idx} value={m.email}>
                                                                {m.email}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormControl fullWidth variant="standard">
                                                    <InputLabel>Risk</InputLabel>
                                                    <Select
                                                        label="Risk"
                                                        name="risk"
                                                        value={massEditData.risk}
                                                        onChange={handleMassEditChange}
                                                    >
                                                        <MenuItem value=""><em>No change</em></MenuItem>
                                                        {RISK_OPTIONS.map((risk) => (
                                                            <MenuItem key={risk} value={risk}>{risk}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Source Url"
                                                    name="sourceUrl"
                                                    variant="standard"
                                                    value={massEditData.sourceUrl}
                                                    onChange={handleMassEditChange}
                                                />
                                            </Grid>
                                        </Grid>

                                        <Button variant="contained" sx={{ mt: 2 }} onClick={handleApplyMassEdit}>
                                            Apply Changes
                                        </Button>
                                    </Box>
                                )}

                                <Box sx={{ mt: 2, textAlign: 'right' }}>
                                    {isSaving ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        <Button variant="contained" onClick={handleSaveAllTrackers}>
                                            Save Trackers
                                        </Button>
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <Typography variant="body1">
                                    No internal trackers found. Create one or import in bulk.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Container>
            </Box>
        </Layout>
    );
};

export default InternalTrackers;
